import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { Button, Form } from "react-bootstrap";
import { FaThumbsUp } from "react-icons/fa";
import { FiFile } from "react-icons/fi";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { useMyUserForm } from "../hooks/useMyUserForm";
import { getPicklistValues } from "../services/myUserService";
import ModalPopup from "@components/Common/ModalPopup";
import Loader from "@features/shared/components/Loader";
import { getUniqueListBy } from "@components/Common/Function";
import styles from "./MyUserDetails.module.css";

const territoryAutoCompletePlaceHolder = {
  SH2_Name__c: "",
  SH2_Code__c: "",
};

/**
 * MyUserDetails Component
 * Main form component for user visit feedback
 * Refactored for better performance and maintainability
 */
const MyUserDetails = () => {
  // Form state and handlers from custom hook
  const {
    userDetails,
    formValidation,
    loading,
    isSubmitModalOpen,
    setIsSubmitModalOpen,
    otherFeedback,
    id,
    queSelect,
    territoryValue,
    setTerritoryValue,
    formCategoryRef,
    formSubCategoryRef,
    file,
    hasFile,
    handleFileRead,
    removeFile,
    isUserNameDisabled,
    onChange,
    onSubmit,
  } = useMyUserForm();

  // Picklist data
  const [categories, setCategories] = useState(null);
  const [subCategories, setSubCategories] = useState(null);

  // Get territory list from Redux
  const territoryOptions = useSelector(
    (state) => state?.territory?.territoryDetails
  );

  // Memoized unique territory list
  const uniqueTerritories = useMemo(() => {
    return territoryOptions?.length === 0
      ? [{ SH2_Name__c: "Loading...", SH2_Code__c: "" }]
      : getUniqueListBy(territoryOptions, "SH2_Code__c");
  }, [territoryOptions]);

  // Load picklist values on mount
  useEffect(() => {
    (async () => {
      const response = await getPicklistValues();
      if (response?.success) {
        setCategories(response?.data?.picklistFieldValues?.Category_es__c);
        setSubCategories(
          response?.data?.picklistFieldValues?.Sub_Category_es__c
        );
      }
    })();
  }, []);

  // Memoized territory change handler
  const handleTerritoryChange = useCallback(
    (e, newValue) => {
      setTerritoryValue(newValue || territoryAutoCompletePlaceHolder);
      onChange(
        {
          target: {
            name: "territory",
            value: `${newValue?.SH2_Name__c}${
              newValue?.SH2_Code__c && `:${newValue?.SH2_Code__c}`
            }`,
          },
        },
        newValue,
        "territory"
      );
    },
    [onChange, setTerritoryValue]
  );

  // Memoized filtered sub-categories
  const filteredSubCategories = useMemo(() => {
    if (!subCategories || otherFeedback === "Any Other Feedback") return [];
    return subCategories.values.filter((name) =>
      name.validFor?.some((index) => index.toString() === id)
    );
  }, [subCategories, id, otherFeedback]);

  // Memoized submit modal close handler
  const handleCloseModal = useCallback(() => {
    setIsSubmitModalOpen(false);
  }, [setIsSubmitModalOpen]);

  return (
    <>
      <div className={styles.myUserDetails__outerBox}>
        {loading && <Loader loading={loading} />}
        
        <div className={styles.myUserDetails__heading}>User Visit Feedback</div>
        
        <div className={styles.myUserDetails__innerBox}>
          <div className={styles.myUserDetails__detailsBox}>
            <div>
              <form className={styles.myUserDetails__form}>
                {/* Mobile Number */}
                <input
                  type="number"
                  name="mobileNumber"
                  placeholder="Enter Mobile Number"
                  className={
                    !userDetails.mobileNumber && formValidation?.mobileNumber
                      ? styles.myUserDetails__inputBoxError
                      : styles.myUserDetails__inputBox
                  }
                  value={userDetails?.mobileNumber}
                  onChange={onChange}
                />
              </form>
              {(!userDetails.mobileNumber ||
                userDetails.mobileNumber?.length !== 10) &&
                formValidation?.mobileNumber && (
                  <p>{formValidation?.mobileNumber}</p>
                )}

              {/* User Name */}
              <form className={styles.myUserDetails__form}>
                <input
                  type="text"
                  name="userName"
                  placeholder="Enter Username"
                  className={
                    !userDetails.userName && formValidation?.userName
                      ? styles.myUserDetails__inputBoxError
                      : styles.myUserDetails__inputBox
                  }
                  value={userDetails?.userName}
                  onChange={onChange}
                  // disabled={isUserNameDisabled}
                />
              </form>
              {!userDetails.userName && formValidation?.userName && (
                <p>{formValidation?.userName}</p>
              )}

              {/* Territory Autocomplete */}
              <Autocomplete
                value={territoryValue}
                id="grouped-demo"
                className="myDealer__categorySelect"
                options={uniqueTerritories}
                getOptionLabel={(option) =>
                  `${option?.SH2_Name__c}${
                    option?.SH2_Code__c && `:${option?.SH2_Code__c}`
                  }`
                }
                onChange={handleTerritoryChange}
                renderInput={(params) => (
                  <TextField {...params} placeholder={"Select Territory"} />
                )}
              />
              {!userDetails?.territory && formValidation?.territory && (
                <p className={styles.myDiary__validation}>
                  {formValidation?.territory}
                </p>
              )}

              {/* Category */}
              <Form.Select
                aria-label="Default select example"
                className={
                  !userDetails.category && formValidation?.category
                    ? styles.myUserDetails__categorySelectError
                    : styles.myUserDetails__categorySelect
                }
                name="category"
                ref={formCategoryRef}
                onChange={onChange}
              >
                <option
                  value=""
                  className={styles.myUserDetails__defaultOption}
                >
                  Category
                </option>
                {categories &&
                  categories.values.map((name, key) => (
                    <option key={key} value={`${key}:${name?.label}`}>
                      {name?.label}
                    </option>
                  ))}
              </Form.Select>
              {!userDetails.category && formValidation?.category && (
                <p>{formValidation?.category}</p>
              )}

              {/* Sub-Category (conditional) */}
              {otherFeedback !== "Any Other Feedback" && (
                <>
                  <Form.Select
                    aria-label="Default select example"
                    className={
                      !userDetails.subCategory && formValidation?.subCategory
                        ? styles.myUserDetails__categorySelectError
                        : styles.myUserDetails__categorySelect
                    }
                    name="subCategory"
                    ref={formSubCategoryRef}
                    onChange={onChange}
                  >
                    <option
                      value=""
                      className={styles.myUserDetails__defaultOption}
                    >
                      Sub-category
                    </option>
                    {filteredSubCategories.map((name, key) => (
                      <option key={key} value={`${key}:${name?.label}`}>
                        {name?.label}
                      </option>
                    ))}
                  </Form.Select>
                  {!userDetails.subCategory && formValidation?.subCategory && (
                    <p>{formValidation?.subCategory}</p>
                  )}
                </>
              )}

              {/* Observation */}
              <textarea
                name="Observation"
                rows="8"
                placeholder="Enter your observation here"
                className={
                  !userDetails.Observation && formValidation?.Observation
                    ? styles.myUserDetails__textareaError
                    : styles.myUserDetails__textarea
                }
                value={userDetails?.Observation}
                onChange={onChange}
              />
              {!userDetails.Observation && formValidation?.Observation && (
                <p>{formValidation?.Observation}</p>
              )}

              <hr />

              {/* Questions Section */}
              <div className={styles.myUserDetails__radioButton}>
                <label className={styles.myUserDetails__questionLabel}>
                  Is the user able to bank and redeem on his / her own?{" "}
                  <sup className={styles.myUserDetails__sup}>*</sup>
                </label>
                <div className={`mb-1 ${styles.myUserDetails__radioOption}`}>
                  <div className="form-check">
                    <input
                      className={styles.myUserDetails__option}
                      type="radio"
                      name="question1"
                      id="flexRadioDefault1"
                      value="yes"
                      onChange={onChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexRadioDefault1"
                    >
                      Yes
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className={styles.myUserDetails__option}
                      type="radio"
                      name="question1"
                      id="flexRadioDefault2"
                      value="no"
                      onChange={onChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexRadioDefault2"
                    >
                      No
                    </label>
                  </div>
                </div>
                {!userDetails.question1 && formValidation?.question1 && (
                  <p>{formValidation?.question1}</p>
                )}
              </div>

              {/* Question 2 (conditional) */}
              {queSelect === "yes" && (
                <div className={styles.myUserDetails__radioButton}>
                  <label className={styles.myUserDetails__questionLabel}>
                    Has the user banked or redeemed himself/herself in last 3
                    months?
                    <sup className={styles.myUserDetails__sup}>*</sup>
                  </label>
                  <div className={styles.myUserDetails__radioOption}>
                    <div className="form-check">
                      <input
                        className={styles.myUserDetails__option}
                        type="radio"
                        name="question2"
                        id="flexRadioDefault3"
                        value="yes"
                        onChange={onChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="flexRadioDefault3"
                      >
                        Yes
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className={styles.myUserDetails__option}
                        type="radio"
                        name="question2"
                        id="flexRadioDefault4"
                        value="no"
                        onChange={onChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="flexRadioDefault4"
                      >
                        No
                      </label>
                    </div>
                  </div>
                  {!userDetails.question2 && formValidation?.question2 && (
                    <p>{formValidation?.question2}</p>
                  )}
                </div>
              )}

              <hr />
            </div>

            {/* File Display */}
            <div className="row">
              {hasFile && (
                <>
                  <div className={styles.myUserDetails__fileUploadTitle}>
                    File uploads
                  </div>
                  <div
                    className={`mb-1 ${styles.myUserDetails__displayFileName}`}
                  >
                    <div>
                      <FiFile className={styles.myUserDetails__fileIcon} />
                      <span className={styles.myUserDetails__fileUploadName}>
                        {file?.fileName}
                      </span>
                    </div>
                    <Button
                      className={styles.myUserDetails__removeUploadFile}
                      onClick={removeFile}
                    >
                      Remove
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* File Upload and Submit */}
            <div className="row">
              <div className="col-6"></div>
              <div className="col-3">
                <div className={styles.myUserDetails__inputWrapper}>
                  Add file
                  <input
                    className={`${styles.myUserDetails__fileInput}`}
                    type="file"
                    name="fileName"
                    accept=".jpg,.png,.pdf,.jpeg"
                    onChange={handleFileRead}
                  />
                </div>
              </div>
              <div className="col-3">
                <Button
                  className={styles.myUserDetails__submitBtn}
                  onClick={onSubmit}
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Success Modal */}
      {isSubmitModalOpen && (
        <ModalPopup
          className="ModalPopup_Submitted"
          isModalOpen={isSubmitModalOpen}
          setIsModalOpen={handleCloseModal}
          title="Submitted Successfully"
          data="User visit feedback has been submitted successfully."
          backdrop={true}
          fade={false}
          icon={
            <div className="submittedIcons draftIcons">
              <FaThumbsUp className="submittedSmall" />
              <FaThumbsUp className="submittedLarge" />
              <FaThumbsUp className="submittedSmall" />
            </div>
          }
        />
      )}
    </>
  );
};

export default React.memo(MyUserDetails);
